import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import AppUtil from "utilities/AppUtil";
import httpRequest from "../../services/httpRequest";
import snakecaseKeys from "snakecase-keys";

// Main URL Endpoint
const _urlPrefix = "/incidents";

export const fetchPageIncident = createAsyncThunk(
  "incident/fetchPageIncident",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}`,
      method: "GET",
      params: AppUtil.toSnakeCaseKey(params),
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const fetchIncidentDetail = createAsyncThunk(
  "incident/fetchIncidentDetail",
  async (params) => {
    const { id } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}`,
      method: "GET",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const createIncident = createAsyncThunk(
  "incident/createIncident",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}`,
      method: "POST",
      data: snakecaseKeys(params),
      successMessage: "toast.create.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const updateIncidentStatus = createAsyncThunk(
  "incident/updateIncidentStatus",
  async (params) => {
    const { id, ...requestData } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}/status`,
      method: "PUT",
      data: snakecaseKeys(requestData),
      successMessage: "toast.update.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const fetchIncidentEvidence = createAsyncThunk(
  "incident/fetchIncidentEvidence",
  async (params) => {
    const { id } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}/evidence`,
      method: "GET",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const uploadIncidentEvidence = createAsyncThunk(
  "incident/uploadIncidentEvidence",
  async (params) => {
    const { id, files, description } = params;
    const data = new FormData();

    if (files && files.length) {
      for (let i = 0; i < files.length; i++) {
        data.append("files", files[i]);
      }
    }

    data.append(
      "content-data",
      new Blob(
        [JSON.stringify(snakecaseKeys({ description }))],
        { type: "application/json" }
      )
    );

    const result = await httpRequest({
      url: `${_urlPrefix}/${id}/evidence`,
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
      data: data,
      successMessage: "toast.add.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const updateIncidentEvidence = createAsyncThunk(
  "incident/updateIncidentEvidence",
  async (params) => {
    const { id, evidenceId, ...requestData } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}/evidence/${evidenceId}`,
      method: "PUT",
      data: snakecaseKeys(requestData),
      successMessage: "toast.update.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const deleteIncidentEvidence = createAsyncThunk(
  "incident/deleteIncidentEvidence",
  async (params) => {
    const { id, evidenceId } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}/evidence/${evidenceId}`,
      method: "DELETE",
      successMessage: "toast.delete.200",
    });
    if (!result) {
      return;
    }
    return { ...result.data, evidenceId };
  }
);

const initialState = {
  list: [],
  totalElements: 0,
  totalPages: 0,
  pageNumber: 1,
  detail: {},
};

const incident = createSlice({
  name: "incident",
  initialState: initialState,
  reducers: {},
  extraReducers: {
    [fetchPageIncident.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.list = payload.content;
        state.totalElements = payload.totalElements;
        state.totalPages = payload.totalPages;
        state.pageNumber = payload.pageNumber;
      }
    },
    [fetchIncidentDetail.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.detail = payload;
      }
    },
    [updateIncidentStatus.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.detail = payload;
      }
    },
  },
});

const { reducer } = incident;
export default reducer;

export const getIncidentList = createSelector(
  (state) => state.incident.list,
  (list) => list
);

export const getIncidentPaging = createSelector(
  (state) => state.incident,
  ({ totalElements, totalPages, pageNumber }) => ({ totalElements, totalPages, pageNumber })
);

export const getIncidentDetail = createSelector(
  (state) => state.incident.detail,
  (detail) => detail
);
